import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  MatchGroupScore,
  MatchSkillFitnessItem,
  Talent,
  TalentsState,
} from "./talentsTypes";
import axios from "axios";
import { API_URL } from "../../config/api";

const BACKEND_API_URL = API_URL;

interface MatchForPositionDTO {
  matchId: string;
  profileId: string;
  status: string;
  overallScore: number;
  isHidden?: boolean;
  skillFitness?: MatchSkillFitnessDTO[];
  groupScores: Record<string, GroupMatchScoreDTO>;
}

interface MatchSkillFitnessDTO {
  name: string;
  score: number;
  isRelevant?: boolean;
}

interface GroupMatchScoreDTO {
  groupName: string;
  weightedAverage: number;
  simpleAverage: number;
  weight: number;
  fields: FieldMatchScoreDTO[];
}

interface FieldMatchScoreDTO {
  fieldName: string;
  score: number;
  weight: number;
  scoringType: string;
  details?: unknown;
}

interface ProfileDTO {
  id: string;
  avatarId: string;
  firstName: string;
  lastName: string;
  headline: string;
  summary: string;
  phones: { country: string; countrySuffix: string; number: string }[];
  location: { country: string; state: string; city: string; address: string };
  emails: string[];
  socialAccounts: { accountType: string; userName: string; url: string }[];
  workExperiences: any[];
  educations: any[];
  certificates: any[];
  skills: { id: string; name: string; point: number }[];
  websites: string[];
}

const normalizeGroupScores = (
  groupScores: Record<string, GroupMatchScoreDTO> | null | undefined
): MatchGroupScore[] => {
  if (!groupScores) return [];
  return Object.values(groupScores).map((group) => ({
    groupName: group.groupName,
    weightedAverage: group.weightedAverage,
    simpleAverage: group.simpleAverage,
    weight: group.weight,
    fields: (group.fields || []).map((field) => ({
      fieldName: field.fieldName,
      score: field.score,
      weight: field.weight,
      scoringType: field.scoringType,
    })),
  }));
};

const normalizeSkillFitness = (
  skillFitness: MatchForPositionDTO["skillFitness"]
): MatchSkillFitnessItem[] => {
  return (skillFitness || []).map((skill) => ({
    name: skill.name,
    score: skill.score,
    isRelevant: skill.isRelevant,
  }));
};

const buildSocialAccounts = (
  accounts: ProfileDTO["socialAccounts"]
): Talent["socialAccounts"] => {
  const base = {
    facebook: "",
    twitter: "",
    linkedin: "",
    instagram: "",
    github: "",
  };

  if (!accounts) return base;

  accounts.forEach((account) => {
    const key = account.accountType?.toLowerCase();
    if (!key) return;
    if (key.includes("facebook")) base.facebook = account.url || account.userName;
    if (key.includes("twitter") || key.includes("x"))
      base.twitter = account.url || account.userName;
    if (key.includes("linkedin")) base.linkedin = account.url || account.userName;
    if (key.includes("instagram"))
      base.instagram = account.url || account.userName;
    if (key.includes("github")) base.github = account.url || account.userName;
  });

  return base;
};

const mapTalentStatus = (
  backendStatus: string | null | undefined,
  _isEligible: boolean
): string => {
  const rawStatus = (backendStatus || "").toLowerCase();
  const normalized =
    rawStatus === "waiting"
      ? "pre-match"
      : rawStatus === "pending"
          ? "created"
          : rawStatus;

  if (normalized === "pre-match") {
    return "requested";
  }
  if (normalized === "accepted") {
    return "accepted";
  }
  if (normalized === "rejected" || normalized === "referred") {
    return "rejected";
  }

  if (normalized === "created") {
    return "";
  }

  return "";
};

const mapMatchToTalent = (
  match: MatchForPositionDTO,
  profile: ProfileDTO,
  positionId: string,
  positionTitle?: string
): Talent => {
  const groupScores = normalizeGroupScores(match.groupScores);
  const eligibilityField = groupScores
    .flatMap((group) => group.fields)
    .find((field) => field.fieldName === "countryEligibility");
  const isEligible = !eligibilityField || eligibilityField.score > 0;
  const backendStatus = (match.status || "").toLowerCase();

  return {
    id: profile.id,
    userId: profile.id,
    firstName: profile.firstName,
    lastName: profile.lastName,
    avatarId: profile.avatarId,
    headline: profile.headline,
    positionTitle,
    summary: profile.summary,
    phones: profile.phones || [],
    emails: profile.emails || [],
    location: {
      country: profile.location?.country || "",
      state: profile.location?.state || "",
      city: profile.location?.city || "",
      address: profile.location?.address || "",
    },
    socialAccounts: buildSocialAccounts(profile.socialAccounts || []),
    websites: profile.websites || [],
    certificates: profile.certificates || [],
    educations: profile.educations || [],
    skills: (profile.skills || []).map((skill) => ({
      name: skill.name,
      point: skill.point,
      matchedSkillId: skill.id || "",
    })),
    workExperiences: profile.workExperiences || [],
    completeness: String(Math.round(match.overallScore ?? 0)),
    createdAt: new Date().toISOString(),
    status: mapTalentStatus(backendStatus, isEligible),
    match: {
      matchId: match.matchId,
      positionId,
      status: backendStatus,
      overallScore: match.overallScore ?? 0,
      isHidden: match.isHidden ?? false,
      skillFitness: normalizeSkillFitness(match.skillFitness),
      groupScores,
    },
  };
};

export const fetchTalentsAsync = createAsyncThunk(
  "talents/fetchTalents",
  async (
    {
      positionId,
      positionTitle,
      limit = 50,
    }: { positionId: string; positionTitle?: string; limit?: number },
    { rejectWithValue }
  ) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return rejectWithValue("No authentication token found");
      }

      if (!positionId) {
        return rejectWithValue("No position ID provided");
      }

      const response = await axios.get(
        `${BACKEND_API_URL}/matching/position/${positionId}/matches`,
        {
          params: { limit },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const matches = Array.isArray(response.data) ? response.data : [];

      const profileRequests = matches.map((match: MatchForPositionDTO) =>
        axios
          .post(
            `${BACKEND_API_URL}/talent/profile/getprofile`,
            { profileId: match.profileId },
            { headers: { Authorization: `Bearer ${token}` } }
          )
          .then((profileResponse) => ({
            match,
            profile: profileResponse.data as ProfileDTO,
          }))
      );

      const profileResults = await Promise.allSettled(profileRequests);
      const resolved = profileResults
        .filter((result): result is PromiseFulfilledResult<{ match: MatchForPositionDTO; profile: ProfileDTO }> =>
          result.status === "fulfilled"
        )
        .map((result) =>
          mapMatchToTalent(
            result.value.match,
            result.value.profile,
            positionId,
            positionTitle
          )
        );

      return resolved;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchTalentByIdAsync = createAsyncThunk(
  "talents/fetchTalentById",
  async (id: string, { getState, rejectWithValue }) => {
    const state = getState() as { talents: TalentsState };
    const talent = state.talents.talents.find((item) => item.id === id);

    if (!talent) {
      return rejectWithValue("Talent not found");
    }

    return talent;
  }
);

const initialState: TalentsState = {
  talents: [],
  selectedTalent: null,
  loading: false,
  error: null,
};

const talentsSlice = createSlice({
  name: "talents",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTalentsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchTalentsAsync.fulfilled,
        (state, action: PayloadAction<Talent[]>) => {
          state.loading = false;
          state.talents = action.payload;
        }
      )
      .addCase(fetchTalentsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ||
          action.error.message ||
          "Failed to fetch talents";
      })
      .addCase(fetchTalentByIdAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchTalentByIdAsync.fulfilled,
        (state, action: PayloadAction<Talent>) => {
          state.loading = false;
          state.selectedTalent = action.payload;
        }
      )
      .addCase(fetchTalentByIdAsync.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ||
          action.error.message ||
          "Failed to fetch talent by ID";
      });
  },
});

export default talentsSlice.reducer;
